import type { jsPDF } from 'jspdf';
import { PreventionsByYearPerMonth, ReportFilters } from '../../../types/types';
import { computePreventionMetrics } from '../../../utils/summary_report/metrics/preventionMetrics';
import { drawSectionHeader, drawMetricCard, ensureSpace } from '../../../utils/summary_report/pdfPrimitives';
import { colors, fonts, layout } from '../../../theme/themes';
import { houseSupportIcon, personIcon } from '../../../utils/summary_report/icon';

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

// Static prose, not from the API — edit the wording/list directly here.
const OTHER_INTERVENTIONS =
  'Other interventions include advocacy (local and high level), resolving disputes ' +
  '(negotiations etc), tenure security support, due diligence support, and awareness ' +
  'and legal counselling.';

/**
 * Draws the prevention/response section — the "flip side" of eviction
 * numbers: same card visual, including YTD and this-month values, plus a
 * callout-style prose block for other interventions.
 */
export function drawPreventionSection(
  doc: jsPDF,
  data: PreventionsByYearPerMonth,
  filters: ReportFilters,
  startY: number,
  contentWidth: number,
  icons?: { households?: string; individuals?: string },
): number {
  const x = layout.pageMarginX;
  const metrics = computePreventionMetrics(data, filters);
  const includesMonth = filters.mode === 'month' || filters.mode === 'both';

  const cardGap = layout.cardGap;
  const cardWidth = (contentWidth - cardGap) / 2;
  const cardHeight = includesMonth ? 120 : 76; // same convention as eviction cards

  // "Other interventions" callout: accent-colored left bar + tinted
  // background + a small caption, so it reads as a highlighted block
  // rather than blending into ordinary body text.
  const calloutPadding = 14;
  const calloutTextWidth = contentWidth - calloutPadding * 2 - 10; // 10 = left accent bar + gap
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(9);
  const proseLines = doc.splitTextToSize(OTHER_INTERVENTIONS, calloutTextWidth) as string[];
  const proseLineHeight = 13;
  const captionHeight = 14;
  const calloutHeight = calloutPadding * 2 + captionHeight + proseLines.length * proseLineHeight;

  const totalSectionHeight = 20 + cardHeight + 14 + calloutHeight + 20;
  const adjustedStartY = ensureSpace(doc, startY, totalSectionHeight);

  let y = drawSectionHeader(
    doc,
    x,
    adjustedStartY,
    contentWidth,
    '03',
    'Ongoing preventive & response efforts by partners',
    'Households and individuals reached, year-to-date and this month',
  );

  drawMetricCard(doc, {
    rect: { x, y, width: cardWidth, height: cardHeight },
    label: 'Households reached by preventive measures.', // placeholder — adjust wording as needed
    ytdValue: formatNumber(metrics.ytdHouseholds),
    mtdValue: includesMonth && metrics.mtdHouseholds !== null ? formatNumber(metrics.mtdHouseholds) : undefined,
    trendPct: metrics.householdsYoyPct,
    icon: houseSupportIcon,
    iconImage: icons?.households,
  });
  drawMetricCard(doc, {
    rect: { x: x + cardWidth + cardGap, y, width: cardWidth, height: cardHeight },
    label: 'Individuals reached by preventive measures.', // placeholder — adjust wording as needed
    ytdValue: formatNumber(metrics.ytdIndividuals),
    mtdValue: includesMonth && metrics.mtdIndividuals !== null ? formatNumber(metrics.mtdIndividuals) : undefined,
    trendPct: metrics.individualsYoyPct,
    icon: personIcon,
    iconImage: icons?.individuals,
  });

  y += cardHeight + 14;

  // Callout block
  doc.setFillColor(...colors.highBg);
  doc.roundedRect(x, y, contentWidth, calloutHeight, layout.cardRadius, layout.cardRadius, 'F');
  doc.setFillColor(...colors.accent);
  doc.rect(x, y, 3, calloutHeight, 'F'); // left accent bar

  const textX = x + calloutPadding + 7; // +7 clears the accent bar with a small gap
  doc.setFont(fonts.body, 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...colors.accent);
  doc.text('OTHER INTERVENTIONS', textX, y + calloutPadding);

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...colors.stone700);
  proseLines.forEach((line, i) => {
    doc.text(line, textX, y + calloutPadding + captionHeight + i * proseLineHeight);
  });

  return y + calloutHeight + 20; // gap before next section
}